import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, LoaderCircle, RefreshCw, ShieldOff } from "lucide-react";
import type { RemoteState } from "../lib/useRemote";

/**
 * 唯一正式远程数据五态：loading / blocked / error / empty / ready。
 * - blocked：权限或安全边界阻断（如 foundation-only），必须说明恢复方法，不用空数据或 0 冒充。
 * - error：说明发生了什么、影响什么、现在能做什么，提供重试。
 * - empty：给出下一步动作，不展示技术实现说明。
 */
export function RemotePageState<T>({ state, onRetry, blocked, empty, emptyHint, renderEmptyData = false, children }: {
  state: RemoteState<T>;
  onRetry: () => void;
  blocked?: { title: string; description: string } | null;
  empty?: ReactNode;
  emptyHint?: string;
  /** 台账等结构化页面在零记录时仍应保留筛选、状态带、表头与领域空态。 */
  renderEmptyData?: boolean;
  children: (data: T) => ReactNode;
}) {
  if (state.status === "loading") {
    return <div className="remote-state" role="status" aria-live="polite">
      <LoaderCircle className="spin" aria-hidden />
      <strong>正在读取控制面数据</strong>
      <span>请求真实服务状态，请稍候。</span>
    </div>;
  }
  if (blocked) {
    return <div className="remote-state blocked" role="alert">
      <ShieldOff aria-hidden />
      <strong>{blocked.title}</strong>
      <span>{blocked.description}</span>
    </div>;
  }
  if (state.status === "error") {
    return <div className="remote-state error" role="alert">
      <AlertTriangle aria-hidden />
      <strong>数据读取失败</strong>
      <span className="remote-state-what">发生了什么：{state.message}</span>
      <span>影响：当前页面的数字和列表可能不是最新状态，无法据此做业务判断。</span>
      <span>现在能做的：稍后重试；如果持续失败，请联系平台技术支持。</span>
      <button className="button secondary" onClick={onRetry}><RefreshCw size={16} />重试</button>
    </div>;
  }
  if (state.status === "empty") {
    if (renderEmptyData) return <>{children(state.data)}</>;
    return <div className="remote-state" role="status">
      <CheckCircle2 aria-hidden />
      <strong>暂无记录</strong>
      <span>{emptyHint ?? "当前查询没有返回记录。"}</span>
      {empty}
    </div>;
  }
  return <>{children(state.data)}</>;
}
